import { keepPreviousData, useQueryClient } from "@tanstack/react-query";

import { $api } from "@/shared/api";

import type { MatchFilters } from "../lib/matchFiltersSchema";
import { toMatchQuery } from "../lib/toMatchQuery";
import { HISTORY_POLL_INTERVAL_MS, isHistorySyncing, isWaitingForHistory } from "./historyPolling";

// История матчей игрока под фильтрами, страницами по 20, новые сверху. Следующая страница —
// по `next_cursor` (`fetchNextPage`). Смена фильтров держит прежний список, пока не пришёл
// новый. Пока бэкенд загружает историю (`history_sync_pending`), список перезапрашивается сам.
export const useMatchHistory = (filters: MatchFilters) => {
  const queryClient = useQueryClient();
  const init = { params: { query: toMatchQuery(filters, new Date()) } };
  const query = $api.useInfiniteQuery("get", "/api/matches", init, {
    pageParamName: "cursor",
    // Не `undefined`: его клиент заменяет на `cursor=0`. `null` в запрос не попадает.
    initialPageParam: null,
    // `null` — страница последняя.
    getNextPageParam: (page) => page.next_cursor,
    placeholderData: keepPreviousData,
    refetchInterval: ({ state }) =>
      isHistorySyncing(state.data?.pages[0], state.dataUpdateCount)
        ? HISTORY_POLL_INTERVAL_MS
        : false,
  });
  // Счётчика ответов нет в результате запроса, он есть только в его состоянии.
  // Компонент перерисовывается на каждый ответ, поэтому значение здесь всегда свежее.
  const fetchCount =
    queryClient.getQueryState($api.queryOptions("get", "/api/matches", init).queryKey)
      ?.dataUpdateCount ?? 0;
  const firstPage = query.data?.pages[0];
  return {
    query,
    items: query.data?.pages.flatMap((page) => page.items) ?? [],
    total: firstPage?.total ?? 0,
    wins: firstPage?.wins ?? 0,
    places: firstPage?.places ?? { known: 0, mvp: 0, top: 0 },
    isWaitingForHistory: isWaitingForHistory(firstPage, fetchCount),
  };
};
